import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { Mail, Send, CheckCircle2, AlertTriangle, Bell } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { ensurePushSubscription } from "@/lib/push";

export default function Settings() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [to, setTo] = useState(user?.email || "");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [pushTesting, setPushTesting] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [pushError, setPushError] = useState("");

  useEffect(() => {
    api.get("/settings/email").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  const handleTest = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    setSending(true);
    try {
      const { data } = await api.post("/settings/email/test", { to });
      setResult(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const handleTestPush = async () => {
    setPushError("");
    setPushResult(null);
    setPushTesting(true);
    try {
      // Ensure user is subscribed (will prompt for permission if not already granted)
      await ensurePushSubscription();
      const { data } = await api.post("/push/test");
      setPushResult(data);
      if (data.sent === 0) {
        setPushError("Keine aktive Push-Subscription gefunden. Bitte über das Glocken-Icon Push-Benachrichtigungen aktivieren.");
      }
    } catch (err) {
      setPushError(formatApiError(err) || "Push-Test fehlgeschlagen — VAPID Public Key konfiguriert? Browser-Berechtigung erteilt?");
    } finally {
      setPushTesting(false);
    }
  };

  return (
    <Layout>
      <div className="px-6 md:px-10 py-8 max-w-3xl mx-auto fade-in">
        <div className="label-eyebrow" style={{ color: "#D4AF37" }}>Konfiguration</div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mt-2">Einstellungen</h1>

        <div className="card p-6 mt-6">
          <div className="flex items-start gap-3">
            <Mail size={20} strokeWidth={1.5} className="text-[#D4AF37] mt-1" />
            <div className="flex-1">
              <h2 className="text-xl font-semibold">E-Mail-Versand</h2>
              <p className="text-sm text-[var(--text-3)] mt-1">Transaktionsmails über Resend.</p>
            </div>
            {cfg && (
              <span
                className={`text-[11px] font-semibold uppercase tracking-wider px-2 py-1 rounded-sm border ${
                  cfg.enabled
                    ? "bg-emerald-500/10 text-emerald-800 border-emerald-500/40"
                    : "bg-[var(--surface-2)] text-[var(--text)] border-zinc-500/30"
                }`}
                data-testid="email-status-badge"
              >
                {cfg.enabled ? "Aktiv" : "Inaktiv"}
              </span>
            )}
          </div>

          {cfg && (
            <div className="grid sm:grid-cols-2 gap-3 mt-6 text-sm">
              <div>
                <div className="label-eyebrow mb-1">Absender</div>
                <div className="text-[var(--text)] font-mono text-sm">{cfg.sender}</div>
              </div>
              <div>
                <div className="label-eyebrow mb-1">Portal-URL</div>
                <div className="text-[var(--text)] font-mono text-sm">{cfg.portal_url}</div>
              </div>
            </div>
          )}

          <form onSubmit={handleTest} className="mt-8 border-t border-[var(--border)] pt-6">
            <div className="label-eyebrow mb-3" style={{ color: "#D4AF37" }}>Test-E-Mail</div>
            <div className="flex gap-3 flex-col sm:flex-row">
              <input
                type="email"
                required
                value={to}
                onChange={(e) => setTo(e.target.value)}
                data-testid="email-test-input"
                placeholder="empfaenger@example.ch"
                className="input-base flex-1"
              />
              <button
                type="submit"
                disabled={sending}
                data-testid="email-test-button"
                className="btn-gold px-5 py-3 rounded-sm text-sm uppercase tracking-wider flex items-center gap-2 justify-center"
              >
                <Send size={16} strokeWidth={1.8} /> {sending ? "Sende…" : "Test senden"}
              </button>
            </div>
            {result && (
              <div className="mt-4 border border-emerald-500/40 bg-emerald-500/10 text-emerald-800 text-sm px-3 py-2 rounded-sm flex items-center gap-2" data-testid="email-test-success">
                <CheckCircle2 size={16} /> Test-E-Mail an <strong>{result.to}</strong> versendet.
              </div>
            )}
            {error && (
              <div className="mt-4 border border-red-500/30 bg-red-500/5 text-red-700 text-sm px-3 py-2 rounded-sm flex items-center gap-2" data-testid="email-test-error">
                <AlertTriangle size={16} /> {error}
              </div>
            )}
          </form>
        </div>

        <div className="card p-6 mt-4">
          <div className="flex items-start gap-3">
            <Bell size={20} strokeWidth={1.5} className="text-[#D4AF37] mt-1" />
            <div className="flex-1">
              <h2 className="text-xl font-semibold">Web-Push Benachrichtigungen</h2>
              <p className="text-sm text-[var(--text-3)] mt-1">Live-Benachrichtigung im Browser/auf dem Handy bei neuen Aufträgen.</p>
            </div>
          </div>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <button
              type="button"
              onClick={handleTestPush}
              disabled={pushTesting}
              data-testid="push-test-button"
              className="btn-gold px-5 py-3 rounded-sm text-sm uppercase tracking-wider flex items-center gap-2 justify-center"
            >
              <Send size={16} strokeWidth={1.8} /> {pushTesting ? "Sende…" : "Test-Push senden"}
            </button>
            <p className="text-xs text-[var(--text-3)] flex-1">
              Sendet eine Test-Benachrichtigung an alle Geräte, auf denen du angemeldet & abonniert bist.
            </p>
          </div>
          {pushResult && pushResult.sent > 0 && (
            <div className="mt-4 border border-emerald-500/40 bg-emerald-500/10 text-emerald-800 text-sm px-3 py-2 rounded-sm flex items-center gap-2" data-testid="push-test-success">
              <CheckCircle2 size={16} /> {pushResult.sent} Test-Push gesendet (auf {pushResult.subscriptions} aktive Subscription{pushResult.subscriptions !== 1 ? "s" : ""}).
            </div>
          )}
          {pushError && (
            <div className="mt-4 border border-red-500/30 bg-red-500/5 text-red-700 text-sm px-3 py-2 rounded-sm flex items-center gap-2" data-testid="push-test-error">
              <AlertTriangle size={16} /> {pushError}
            </div>
          )}
        </div>

        <div className="card p-6 mt-4">
          <h3 className="text-lg font-semibold">DNS für nivaragroup.ch (Resend)</h3>
          <p className="text-sm text-[var(--text-3)] mt-1">Damit E-Mails von <code className="text-[#D4AF37] font-mono">main@nivaragroup.ch</code> nicht im Spam landen, im Resend-Dashboard die Domain hinzufügen und die SPF/DKIM-Records bei Ihrem Registrar eintragen.</p>
          <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="text-sm text-[#D4AF37] hover:text-[var(--text)] mt-3 inline-block uppercase tracking-wider">Resend-Dashboard öffnen →</a>
        </div>
      </div>
    </Layout>
  );
}
